import { useEffect, useState } from 'react';
import * as api from './api';
import type { HealthResponse } from './api';
import { ToastHost } from './components/ToastHost';
import { DialogProvider } from './components/Dialogs';
import { APP_NAME } from './config';
import { DemoPage } from './pages/DemoPage';
import { LivePage } from './pages/LivePage';
import { AppProvider, useApp } from './store';

const TABS = [
  { id: 'live', label: 'Live' },
  { id: 'demo', label: 'Demo' },
] as const;

type TabId = (typeof TABS)[number]['id'];

function isTabId(value: string): value is TabId {
  return TABS.some((t) => t.id === value);
}

function currentTabFromHash(): TabId {
  const hash = window.location.hash.replace(/^#\/?/, '');
  return isTabId(hash) ? hash : 'live';
}

function Shell() {
  const { connection } = useApp();
  const [tab, setTab] = useState<TabId>(currentTabFromHash);
  const [health, setHealth] = useState<HealthResponse | null>(null);

  useEffect(() => {
    document.title = APP_NAME;
  }, []);

  useEffect(() => {
    const onHashChange = () => setTab(currentTabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const poll = () => {
      api
        .getHealth()
        .then((res) => {
          if (!cancelled) setHealth(res);
        })
        .catch(() => {
          if (!cancelled) setHealth(null);
        });
    };
    poll();
    const timer = window.setInterval(poll, 15000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  const select = (next: TabId) => {
    window.location.hash = `#/${next}`;
    setTab(next);
  };

  return (
    <div className="app">
      <header className="app__bar">
        <div className="brand">
          <span className="brand__dot" />
          <strong>{APP_NAME}</strong>
        </div>

        <nav className="tabs" aria-label="Sections">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              className={`tab${tab === t.id ? ' is-active' : ''}`}
              aria-current={tab === t.id ? 'page' : undefined}
              onClick={() => select(t.id)}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="app__status">
          <span className={`pill pill--${connection}`} title="Server-sent events connection">
            {connection === 'open' ? 'live' : connection}
          </span>
          {health ? (
            <span className="muted">
              {health.reports} reports · {health.fingerprints} fp · {health.floors} floors
            </span>
          ) : (
            <span className="muted">backend offline</span>
          )}
        </div>
      </header>

      <main className="app__main">
        {tab === 'live' ? <LivePage /> : null}
        {tab === 'demo' ? (
          <div className="app__scroll">
            <DemoPage />
          </div>
        ) : null}
      </main>

      <ToastHost />
    </div>
  );
}

export function App() {
  return (
    <AppProvider>
      <DialogProvider>
        <Shell />
      </DialogProvider>
    </AppProvider>
  );
}
